import { type ReactElement, type ReactNode } from 'react';
import { Routes, Route, Navigate, Outlet } from 'react-router';
import type { MeResponse } from '@citibox-panel/api-client';
import { Box } from '@citibox-panel/ui';
import { RequireAuth, isAllowedAccount, useAuthContext } from '@citibox-panel/auth';
import { LoginPage } from '@/features/login';
import { HomeView } from '@/features/home';
import { UiKitPage } from '@/features/ui-kit';
import { AmazonDoorAccessRoute } from '@/features/amazon-door-access';
import { CarrierExclusionListsRoute } from '@/features/carrier-exclusion-lists';
import { fetchMe as fetchMeFromApi } from '@/lib/apiClient';
import { AccessStateScreen } from './components/AccessStateScreen';
import { ForeignTabsGuard } from './components/ForeignTabsGuard';
import { MockModeBanner } from './components/MockModeBanner';
import { RequireView, type ViewHome } from './components/RequireView';
import { SessionGate } from './components/SessionGate';

type FetchMe = () => Promise<MeResponse>;

// The landing view, and the permission that must hold for «Ir al inicio» to lead anywhere.
const HOME: ViewHome = { to: '/', permission: 'care.search' };

function LayoutFrame({ children }: { children: ReactNode }): ReactElement {
  return (
    // A column, so a full-screen state centres in the space left under the mock banner instead of
    // in 100vh, and the page does not scroll by the banner's height.
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {/* Above the landmark and above every surface: a deployment serving fixtures looks exactly
          like a real one, and a batch write against the in-memory store reports a success that
          never happened. Rendered here so it costs no slice any knowledge of mocks. */}
      <MockModeBanner />
      <Box component="main" sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        {children}
      </Box>
    </Box>
  );
}

function AuthenticatedSession({ fetchMe }: { fetchMe: FetchMe }): ReactElement | null {
  const { firebaseUser } = useAuthContext();
  // An account outside the domain can be reported for a moment, before the sign-in signs it out
  // again: it gets nothing here, not even a /me read. The server must still reject its token.
  if (firebaseUser === null || !isAllowedAccount(firebaseUser.email)) return null;
  return (
    <ForeignTabsGuard uid={firebaseUser.uid}>
      {/* Keyed by uid: nothing mounted for the previous person survives into the next session. */}
      <SessionGate key={firebaseUser.uid} uid={firebaseUser.uid} email={firebaseUser.email} fetchMe={fetchMe}>
        <Outlet />
      </SessionGate>
    </ForeignTabsGuard>
  );
}

// Auth gate + main landmark around the routed content. Option C: the navigation chrome (header,
// search tabs, user menu) lives in the search shell rendered by the home view, not in this layout.
function AuthenticatedLayout({ fetchMe }: { fetchMe: FetchMe }): ReactElement {
  return (
    <RequireAuth
      fallback={
        <LayoutFrame>
          <AccessStateScreen variant="loading" />
        </LayoutFrame>
      }
    >
      <LayoutFrame>
        <AuthenticatedSession fetchMe={fetchMe} />
      </LayoutFrame>
    </RequireAuth>
  );
}

export interface AppRoutesProps {
  fetchMe?: FetchMe;
}

// The single route table: URL → slice component. Slices stay URL-agnostic; every path string lives
// here.
export function AppRoutes({ fetchMe = fetchMeFromApi }: AppRoutesProps): ReactElement {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/ui-kit" element={<UiKitPage />} />
      <Route element={<AuthenticatedLayout fetchMe={fetchMe} />}>
        <Route
          path="/"
          element={
            <RequireView permission="care.search" home={HOME}>
              <HomeView />
            </RequireView>
          }
        />
        {/* Reached by direct URL only: deliberately linked from nowhere in the panel. */}
        <Route
          path="/amazon/door-access"
          element={
            <RequireView permission="operations.amazon_door_access" home={HOME}>
              <AmazonDoorAccessRoute />
            </RequireView>
          }
        />
        {/* Reached by direct URL only, like the gate above: Producto's screen, never linked for
            Customer Care. */}
        <Route
          path="/carriers/exclusion-lists"
          element={
            <RequireView permission="operations.carrier_exclusion_lists" home={HOME}>
              <CarrierExclusionListsRoute />
            </RequireView>
          }
        />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
